import { Box, Grid } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { ANTRAG_TYP, ANTRAGS_FORMULAREN, KOSTEN_ARTEN, KOSTEN_POSITION } from 'src/lib/constants/constant';
import { DisplayDecimal, DisplayEuro } from 'src/utils/formats';
import { getYearlySums, getZielFoerderQuote } from '../../../../application-helper-function/AppHelperfunctions';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const KP_P = KostenPositionSchema.attributes;
const P = AntragSchema.attributes;

const EinnahmenHinweisGlobal: FC<{
	kostentraeger: string;
	unternehmenId?: number | undefined;
	institutionsTyp?: string | undefined;
	selectedOrgaName?: string | undefined;
}> = ({ kostentraeger, unternehmenId, institutionsTyp, selectedOrgaName }) => {
	const { application } = useApplication();

	const currentInstitutionsTyp = institutionsTyp ? institutionsTyp : get(application, P.Organisation_Antragsberechtigt_Als.path);

	const anZahlEinahmen = useMemo(() => {
		// autofold/fold_next
		const allKostenPostionFiltered = get(application, P.Foerderantrag_Kostenpositions.path, []).filter(
			(ele: any) =>
				get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme &&
				get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel &&
				(unternehmenId ? get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId : true),
		);
		return allKostenPostionFiltered.length;
	}, [application, unternehmenId]);
	const ausgabeSumme = useMemo(() => {
		// autofold/fold_next
		const allKostenPositionPartnerFiltered = get(application, P.Foerderantrag_Kostenpositions.path, []).filter(
			(ele: any) =>
				(unternehmenId ? get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId : true) &&
				get(ele, KP_P.Allgemein_Kostenposition.path, '') === KOSTEN_POSITION.Ausgabe,
		);
		if (allKostenPositionPartnerFiltered.length > 0) {
			return allKostenPositionPartnerFiltered.reduce((sum: any, ele: any) => {
				return sum + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
			}, 0);
		}
		return 0;
	}, [application, unternehmenId]);
	const einnahmeSumme = useMemo(() => {
		// autofold/fold_next
		const allKostenPositionPartnerFiltered = get(application, P.Foerderantrag_Kostenpositions.path, []).filter(
			(ele: any) =>
				(unternehmenId ? get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId : true) &&
				get(ele, KP_P.Allgemein_Kostenposition.path, '') === KOSTEN_POSITION.Einnahme,
		);
		if (allKostenPositionPartnerFiltered.length > 0) {
			return allKostenPositionPartnerFiltered.reduce((sum: any, ele: any) => {
				return sum + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
			}, 0);
		}
		return 0;
	}, [application, unternehmenId]);
	const foerderquote = useMemo(() => {
		// autofold/fold_next
		const foerderQuote = ((ausgabeSumme - einnahmeSumme) / ausgabeSumme) * 100;
		return foerderQuote || 0;
	}, [ausgabeSumme, einnahmeSumme]);
	const zielFoerderquote = useMemo(() => {
		// autofold/fold_next
		return getZielFoerderQuote(application, currentInstitutionsTyp);
	}, [application, currentInstitutionsTyp]);

	const antragsformular = get(application, P.Allgemein_Antragsformular.path);
	const antragTyp = get(application, P.Allgemein_Typ.path);

	const kostenpositionYearlyData = useMemo(() => {
		if (antragsformular !== ANTRAGS_FORMULAREN.Loewe) return [];

		const filteredPositionen = get(application, P.Foerderantrag_Kostenpositions.path, []).filter((ele: any) =>
			unternehmenId ? get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId : true,
		);

		const ausgabeByYear = getYearlySums(filteredPositionen, KOSTEN_POSITION.Ausgabe);
		const einnahmeByYear = getYearlySums(filteredPositionen, KOSTEN_POSITION.Einnahme);
		const years = Array.from(new Set([...Object.keys(ausgabeByYear), ...Object.keys(einnahmeByYear)].map((year) => Number(year))))
			.filter((year) => !Number.isNaN(year))
			.sort((a, b) => a - b);

		return years.map((year) => {
			const ausgabe = ausgabeByYear[year] || 0;
			const einnahme = einnahmeByYear[year] || 0;
			const foerderquoteYear = ausgabe > 0 ? ((ausgabe - einnahme) / ausgabe) * 100 : 0;

			return {
				year,
				ausgabe,
				einnahme,
				foerderquote: foerderquoteYear || 0,
			};
		});
	}, [antragsformular, application, unternehmenId]);

	const getAlertContent = () => {
		switch (antragsformular) {
			case ANTRAGS_FORMULAREN.StartUpStipendium:
				return {
					title: undefined,
					message: `Gesamtausgaben des ${kostentraeger}s: ${DisplayEuro(ausgabeSumme)}`,
				};
			case ANTRAGS_FORMULAREN.Loewe:
				//Nur bei Skizze. Antrag wird schon abgefangen
				return {
					title: `Gesamtausgaben ${selectedOrgaName}: ${DisplayEuro(ausgabeSumme)}`,
					message: `Berechnete Förderquote ${selectedOrgaName}: ${DisplayDecimal(foerderquote)} % ${zielFoerderquote > 0 ? '(Maximale Förderquote: ' + DisplayDecimal(zielFoerderquote) + ' %)' : ''}`,
				};
			default:
				return {
					title: `Gesamtausgaben des ${kostentraeger}s: ${DisplayEuro(ausgabeSumme)}`,
					message: `Berechnete Förderquote des ${kostentraeger}s: ${DisplayDecimal(foerderquote)} % ${zielFoerderquote > 0 ? '(Maximale Förderquote: ' + DisplayDecimal(zielFoerderquote) + ' %)' : ''}`,
				};
		}
	};

	const alertContent = getAlertContent();

	// Für Loewe muss Gesamtausgabe/Foerderquote pro Jahr berechnet bei Antrag
	if (antragsformular === ANTRAGS_FORMULAREN.Loewe && antragTyp === ANTRAG_TYP.Antrag) {
		return (
			<Grid container spacing={2}>
				{kostenpositionYearlyData.map((ele) => (
					<Grid key={ele.year} item xs={12} md={6} xl={4}>
						<MUIAlert
							title={`${ele.year}`}
							message={`Gesamtausgaben: ${DisplayEuro(ele.ausgabe)} ${ele.ausgabe > 0 && ele.einnahme > 0 ? ` \n\n Berechnete Förderquote: ${DisplayDecimal(ele.foerderquote)} % ${zielFoerderquote > 0 ? '(Maximale Förderquote: ' + DisplayDecimal(zielFoerderquote) + ' %)' : ''}` : ''}`}
						/>
						{ele.einnahme > 0 && ele.ausgabe <= ele.einnahme && (
							<Box sx={{ mt: '1rem' }}>
								<MUIAlert
									infoType={'error'}
									message={`Ihre Eigenmittel liegen über den Gesamtausgaben des ${kostentraeger}s im Jahr ${ele.year}. Einnahmen gesamt: ${DisplayEuro(ele.einnahme)}`}
								/>
							</Box>
						)}
					</Grid>
				))}
			</Grid>
		);
	}

	return (
		<>
			{anZahlEinahmen > 0 && ausgabeSumme > 0 && einnahmeSumme > 0 && (
				<Box sx={{ mt: '1rem' }}>
					<MUIAlert title={alertContent.title} message={alertContent.message} />
				</Box>
			)}
			{anZahlEinahmen > 0 && einnahmeSumme > 0 && (
				<Box sx={{ mt: '1rem' }}>
					{ausgabeSumme <= einnahmeSumme && (
						<MUIAlert
							infoType={'error'}
							message={`Ihre Eigenmittel liegen über den Gesamtausgaben des ${kostentraeger}s. Einnahmen gesamt: ${DisplayEuro(einnahmeSumme)}`}
						/>
					)}
				</Box>
			)}
		</>
	);
};

export default EinnahmenHinweisGlobal;
